// Valid event triggers
const validTriggers = {
  focus: true,
  hover: true,
  click: true,
  blur: true,
  manual: true
}

// Directive modifier test regular expressions. Pre-compile for performance
const htmlRE = /^html$/i
const noninteractiveRE = /^noninteractive$/i
const placementRE = /^(auto|top(-start|-end)?|bottom(-start|-end)?|left(-start|-end)?|right(-start|-end)?)$/i
const boundaryRE = /^(window|viewport|scrollParent)$/i
const delayRE = /^d\d+$/i
const delayShowRE = /^ds\d+$/i
const delayHideRE = /^dh\d+$/i
const offsetRE = /^o-?\d+$/i
const variantRE = /^v-.+$/i
const spacesRE = /\s+/

export const isPlainObject = obj => Object.prototype.toString.call(obj) === '[object Object]'

// Build a Tooltip config based on bindings (if any)
// Arguments and modifiers take precedence over passed value config object
const parseTooltipBindings = (bindings, vnode) => {
  // We start out with a basic config
  let config = {
    title: undefined,
    content: undefined, // this will be used for popover.
    trigger: '', // Default set below if needed
    placement: 'top',
    fallbackPlacement: 'flip',
    container: false, // Default of body
    offset: 0,
    id: null,
    html: false,
    interactive: true,
    disabled: false,
    delay: 50,
    boundary: 'scrollParent',
    boundaryPadding: 5,
    customClass: ''
  }
  // Process `bindings.value`
  if (typeof bindings.value === 'string' || typeof bindings.value === 'number') {
    // Value is tooltip content (HTML optionally supported)
    config.title = bindings.value
  } else if (typeof bindings.value === 'function') {
    // Title generator function
    config.title = bindings.value
  } else if (isPlainObject(bindings.value)) {
    // Value is config object, so merge
    config = { ...config, ...bindings.value }
  }

  if (!config.title) {
    // see if it is on the title prop
    config.title = vnode?.props?.title
  }

  if (!config.content) {
    config.content = vnode?.props?.content
  }

  if (config.noninteractive) {
    config.interactive = false
  }

  if (!isPlainObject(config.delay)) {
    config.delay = {
      show: parseInt(config.delay),
      hide: parseInt(config.delay)
    }
  }

  // Process modifiers
  Object.keys(bindings.modifiers).forEach(mod => {
    if (htmlRE.test(mod)) {
      // Title allows HTML
      config.html = true
    } else if (noninteractiveRE.test(mod)) {
      // Noninteractive
      config.interactive = false
    } else if (placementRE.test(mod)) {
      // Placement of tooltip
      config.placement = mod
    } else if (boundaryRE.test(mod)) {
      // Boundary of tooltip
      mod = mod === 'scrollparent' ? 'scrollParent' : mod
      config.boundary = mod
    } else if (delayRE.test(mod)) {
      // Delay value
      const delay = parseInt(mod.slice(1))
      config.delay.show = delay
      config.delay.hide = delay
    } else if (delayShowRE.test(mod)) {
      // Delay show value
      config.delay.show = parseInt(mod.slice(2))
    } else if (delayHideRE.test(mod)) {
      // Delay hide value
      config.delay.hide = parseInt(mod.slice(2))
    } else if (offsetRE.test(mod)) {
      // Offset value, negative allowed
      config.offset = parseInt(mod.slice(1))
    } else if (variantRE.test(mod)) {
      // Variant
      config.variant = mod.slice(2) || null
    }
  })

  // Special handling of event trigger modifiers trigger is
  // a space separated list
  const selectedTriggers = {}
  const concat = (...args) => Array.prototype.concat.apply([], args)
  // Parse current config object trigger
  concat(config.trigger || '')
    .filter(x => x)
    .join(' ')
    .trim()
    .toLowerCase()
    .split(spacesRE)
    .forEach(trigger => {
      if (validTriggers[trigger]) {
        selectedTriggers[trigger] = true
      }
    })

  // Parse modifiers for triggers
  Object.keys(bindings.modifiers).forEach(mod => {
    mod = mod.toLowerCase()
    if (validTriggers[mod]) {
      // If modifier is a valid trigger
      selectedTriggers[mod] = true
    }
  })

  // Sanitize triggers
  config.trigger = Object.keys(selectedTriggers).join(' ')
  if (config.trigger === 'blur') {
    // Blur by itself is useless, so convert it to 'focus'
    config.trigger = 'focus'
  }
  if (!config.trigger) {
    // Use default trigger
    config.trigger = 'hover focus'
  }

  // Return the config
  return config
}

export default parseTooltipBindings
